/**
 * Spreadsheets, in the browser.
 *
 * The customer's questionnaire is parsed here and never uploaded. Only the
 * question text is sent to the server, and only after they have seen which
 * column it came from. That is not a detail: "where does our questionnaire go"
 * is question 47 of most questionnaires, and the honest answer being "nowhere"
 * is worth more than the engineering it costs.
 *
 * SheetJS is pinned from the vendor's own CDN tarball rather than the `xlsx`
 * package on npm, which stopped at 0.18.5 and carries unpatched prototype
 * pollution advisories. A security product cannot ship that dependency.
 */

import type { SheetRows } from '../core/questions';

/**
 * SheetJS is loaded on demand. It is by far the largest dependency here and it
 * is only needed on the two screens that touch a file, so the sign-in page and
 * the trust page should not pay for it.
 */
const sheetjs = () => import('xlsx');

export interface LoadedSheet {
  name: string;
  rows: SheetRows;
}

export interface LoadedWorkbook {
  filename: string;
  sheets: LoadedSheet[];
}

const MAX_ROWS = 5000;

export async function readWorkbook(file: File): Promise<LoadedWorkbook> {
  const XLSX = await sheetjs();
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });

  const sheets: LoadedSheet[] = workbook.SheetNames.map((name) => {
    const sheet = workbook.Sheets[name];
    // `raw: false` renders dates and numbers the way the author formatted them,
    // which is what a person reading the mapping preview expects to see.
    const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      raw: false,
      defval: '',
      blankrows: true,
    });
    return {
      name,
      rows: rows.slice(0, MAX_ROWS).map((row) => row.map((cell) => String(cell ?? ''))),
    };
  });

  return { filename: file.name, sheets };
}

export interface AnswerRow {
  rowIndex: number;
  status: string;
  answerText: string;
  citations: string[];
}

function citationLabel(id: string, titles: Map<string, string>): string {
  return titles.get(id) ?? id;
}

/**
 * Writes answers back into the customer's own workbook.
 *
 * Two columns are appended after the last one in use, so their sheet keeps its
 * shape, its other columns, and its row order — the point is to hand back the
 * file they sent, filled in, not a different file they now have to reconcile.
 */
export async function fillWorkbook(
  file: File,
  sheetName: string,
  answers: AnswerRow[],
  evidenceTitles: Map<string, string>,
): Promise<Blob> {
  const XLSX = await sheetjs();
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellStyles: true });
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) throw new Error('sheet_not_found');

  const range = XLSX.utils.decode_range(sheet['!ref'] ?? 'A1');
  // Two columns appended after the last one in use. `sheet_add_aoa` writes the
  // pair from a single origin, so only the first index is needed.
  const answerCol = range.e.c + 1;

  XLSX.utils.sheet_add_aoa(sheet, [['Answer', 'Evidence']], {
    origin: { r: range.s.r, c: answerCol },
  });

  for (const answer of answers) {
    const text =
      answer.status === 'answered'
        ? answer.answerText
        : answer.status === 'unsupported'
          ? 'NO SUPPORTING EVIDENCE — do not send'
          : '';
    const cites =
      answer.status === 'answered'
        ? answer.citations.map((id) => citationLabel(id, evidenceTitles)).join('; ')
        : '';

    XLSX.utils.sheet_add_aoa(sheet, [[text, cites]], {
      origin: { r: answer.rowIndex, c: answerCol },
    });
  }

  const out = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/** A standalone workbook, for when the original file is no longer to hand. */
export async function buildAnswerWorkbook(
  rows: { question: string; status: string; answerText: string; citations: string[] }[],
  evidenceTitles: Map<string, string>,
): Promise<Blob> {
  const XLSX = await sheetjs();
  const aoa: string[][] = [['Question', 'Answer', 'Evidence', 'Status']];
  for (const row of rows) {
    aoa.push([
      row.question,
      row.status === 'answered' ? row.answerText : '',
      row.status === 'answered'
        ? row.citations.map((id) => citationLabel(id, evidenceTitles)).join('; ')
        : '',
      row.status === 'answered'
        ? 'Answered'
        : row.status === 'unsupported'
          ? 'No supporting evidence'
          : 'Not drafted',
    ]);
  }

  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  sheet['!cols'] = [{ wch: 60 }, { wch: 70 }, { wch: 30 }, { wch: 22 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Answers');

  const out = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
